import { getSignedUrl } from "@/shared/requests/get-signed-url";
import { WorkBook } from "xlsx";

export const createJSONForExcelMapper = async (
  file: File,
): Promise<{
  data: string[][];
  merges: { s: { r: number; c: number }; e: { r: number; c: number } }[];
}> => {
  const { read, utils } = await import("xlsx");
  const resultPromise = new Promise<{
    data: string[][];
    merges: { s: { r: number; c: number }; e: { r: number; c: number } }[];
  }>((resolve, reject) => {
    const fileReader = new FileReader();
    const isCsv = file.type === "text/csv";

    fileReader.onload = (e) => {
      const content = e.target?.result;

      if (!content) {
        reject("Error reading file");
        return;
      }

      if (isCsv && typeof content === "string") {
        const rows = content.split("\n");
        const data = rows.map((row) => row.split(","));
        resolve({ data, merges: [] });
      } else {
        const workbook = read(content, { type: "binary" });
        const sheetNames = filterVisibleSheets(workbook);
        const sheetName = sheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const data: string[][] = utils.sheet_to_json(sheet, {
          header: 1,
          blankrows: false,
          defval: "",
        });
        const merges = sheet["!merges"] ?? [];
        resolve({ data: data.slice(0, 50), merges });
      }
    };

    fileReader.onerror = () => reject("Error reading file");

    if (isCsv) {
      fileReader.readAsText(file);
    } else {
      fileReader.readAsArrayBuffer(file);
    }
  });

  return await resultPromise;
};

export const getFileContent = async (
  file: File,
): Promise<{
  content: string | ArrayBuffer;
  isCsv: boolean;
}> => {
  const resultPromise = new Promise<{
    content: string | ArrayBuffer;
    isCsv: boolean;
  }>((resolve, reject) => {
    const fileReader = new FileReader();
    const isCsv = file.type === "text/csv";

    fileReader.onload = (e) => {
      const content = e.target?.result;

      if (!content) {
        reject("Error reading file");
        return;
      }

      resolve({
        content,
        isCsv,
      });
    };

    fileReader.onerror = () => reject("Error reading file");

    if (isCsv) {
      fileReader.readAsText(file);
    } else {
      fileReader.readAsArrayBuffer(file);
    }
  });

  return await resultPromise;
};

export const getSheetData = async (content: string | ArrayBuffer) => {
  const { read } = await import("xlsx");
  const resultPromise = new Promise<{
    sheetNames: string[];
    workbook: WorkBook;
  }>((resolve, reject) => {
    const workbook = read(content, { type: "binary" });

    if (!workbook) {
      reject("Error reading file");
      return;
    }

    const sheetNames = filterVisibleSheets(workbook);
    resolve({
      sheetNames,
      workbook,
    });
  });
  return resultPromise;
};


export const getSheetDataFromUrl = async (url: string): Promise<{
  sheetNames: string[];
  workbook: WorkBook;
}> => {
  const { read } = await import("xlsx");

  const tryFetchWorkbook = async (fileUrl: string): Promise<{ sheetNames: string[]; workbook: WorkBook }> => {
    const response = await fetch(fileUrl);

    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.statusText}`);
    }

    const buffer = await response.arrayBuffer();
    const workbook = read(buffer, { type: "binary" });

    if (!workbook) {
      throw new Error("Error reading file");
    }

    const sheetNames = filterVisibleSheets(workbook);
    return { sheetNames, workbook };
  };

  try {
    // First attempt
    return await tryFetchWorkbook(url);
  } catch (error) {
    // If it fails, get a new file and try once more
    const newSignedUrl = await getSignedUrl({
      stale_urls: [url],
          max_age: 86400,
        });
    return await tryFetchWorkbook(newSignedUrl[0]);
  }
};

export const getJSONFromSheet = async (
  workbook: WorkBook,
  sheetName: string,
) => {
  const { utils } = await import("xlsx");

  const resultPromise = new Promise<{
    data: string[][];
    merges: { s: { r: number; c: number }; e: { r: number; c: number } }[];
  }>((resolve) => {
    const sheet = workbook.Sheets[sheetName];
    const rawData: string[][] = utils.sheet_to_json(sheet, {
      header: 1,
      blankrows: true,
      defval: "",
    });

    // Omit empty rows by finding index of last row with data
    let lastNonEmptyIndex = rawData.length - 1;
    while (lastNonEmptyIndex >= 0) {
      const row = rawData[lastNonEmptyIndex];
      if (row.some((cell) => typeof cell === "string" ? cell.trim() !== "" : cell !== undefined && cell !== null && cell !== "")) {
        break;
      }
      lastNonEmptyIndex--;
    }
    const trimmedData = rawData.slice(0, lastNonEmptyIndex + 1);

    const merges = sheet["!merges"] ?? [];
    resolve({ data: trimmedData, merges });
  });
  return resultPromise;
};

const filterVisibleSheets = (workbook: WorkBook): string[] => {
  if (!workbook.Workbook || !workbook.Workbook.Sheets) {
    return workbook.SheetNames;
  }

  return workbook.SheetNames.map((sheetName, index) => {
    const sheetVisibility = workbook.Workbook?.Sheets?.[index]?.Hidden;
    if (sheetVisibility === 1 || sheetVisibility === 2) {
      return `${sheetName}::hidden`;
    }
    return sheetName;
  });
};
